
import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './HotelBookingForm.css';

const HotelBookingForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    idType: '',
    idNumber: '',
    checkIn: new Date(),
    checkOut: new Date(new Date().setDate(new Date().getDate() + 1)), // Set default checkout to next day
    adults: 1,
    children: 0,
    childrenAges: [], // New field for children's ages
    priceRange: '',
    roomType: '',
    specialRequests: ''
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alertVariant, setAlertVariant] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');

  // Update children's ages array when number of children changes
  useEffect(() => {
    const newChildrenAges = Array(formData.children).fill('');
    setFormData(prev => ({
      ...prev,
      childrenAges: newChildrenAges
    }));
  }, [formData.children]);

  const priceRanges = [
    "₹1000 - ₹2000",
    "₹2000 - ₹3000",
    "₹3000 - ₹5000",
    "₹5000 - ₹7000",
    "₹7000+"
  ];

  const roomTypes = [
    "Standard Room",
    "Deluxe Room",
    "Suite",
    "Family Room",
    "Villa"
  ];

  const idTypes = [
    "Aadhar Card",
    "Passport",
    "Driving License",
    "Voter ID",
    "PAN Card"
  ];

  const handleChildAgeChange = (index, age) => {
    const newChildrenAges = [...formData.childrenAges];
    newChildrenAges[index] = age;
    setFormData({
      ...formData,
      childrenAges: newChildrenAges
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/book-hotel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setAlertVariant('success');
        setAlertMessage('Hotel booking request sent successfully! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          idType: '',
          idNumber: '',
          checkIn: new Date(),
          checkOut: new Date(),
          adults: 1,
          children: 0,
          childrenAges: [],
          priceRange: '',
          roomType: '',
          specialRequests: ''
        });
      } else {
        throw new Error(data.message || 'Something went wrong');
      }
    } catch (error) {
      setAlertVariant('danger');
      setAlertMessage('Failed to send booking request. Please try again.');
    }
    setShowAlert(true);
  };

  // Handle check-in date change
  const handleCheckInChange = (date) => {
    setFormData(prev => {
      // Calculate minimum check-out date (next day after check-in)
      const minCheckOut = new Date(date);
      minCheckOut.setDate(date.getDate() + 1);

      // If current check-out is before minimum, update it
      const newCheckOut = prev.checkOut < minCheckOut ? minCheckOut : prev.checkOut;

      return {
        ...prev,
        checkIn: date,
        checkOut: newCheckOut
      };
    });
  };

  // Handle check-out date change
  const handleCheckOutChange = (date) => {
    setFormData(prev => ({
      ...prev,
      checkOut: date
    }));
  };

  return (
    <div className="hotel-booking-form-container">
      <h3>Hotel Booking Request</h3>
      
      {showAlert && (
        <Alert 
          variant={alertVariant} 
          onClose={() => setShowAlert(false)} 
          dismissible
        >
          {alertMessage}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            required
          />
        </Form.Group>

        <div className="row">
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>ID Type</Form.Label>
              <Form.Select
                value={formData.idType}
                onChange={(e) => setFormData({...formData, idType: e.target.value})}
                required
              >
                <option value="">Select ID Type</option>
                {idTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>ID Number</Form.Label>
              <Form.Control
                type="text"
                value={formData.idNumber}
                onChange={(e) => setFormData({...formData, idNumber: e.target.value})}
                required
              />
            </Form.Group>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Check-in Date</Form.Label>
              <DatePicker
                selected={formData.checkIn}
                onChange={handleCheckInChange}
                className="form-control"
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                required
              />
              <Form.Text className="text-muted">
                Check-in time: 12:00 PM
              </Form.Text>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Check-out Date</Form.Label>
              <DatePicker
                selected={formData.checkOut}
                onChange={handleCheckOutChange}
                className="form-control"
                minDate={new Date(formData.checkIn.getTime() + 24 * 60 * 60 * 1000)} // Next day after check-in
                dateFormat="dd/MM/yyyy"
                required
              />
              <Form.Text className="text-muted">
                Check-out time: 11:00 AM
              </Form.Text>
            </Form.Group>
          </div>
        </div>

        {/* Add stay duration display */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="stay-duration">
              Stay Duration: {Math.ceil((formData.checkOut - formData.checkIn) / (1000 * 60 * 60 * 24))} night(s)
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Number of Adults</Form.Label>
              <Form.Control
                type="number"
                value={formData.adults}
                onChange={(e) => setFormData({...formData, adults: Math.max(1, parseInt(e.target.value) || 0)})}
                min="1"
                required
              />
              <Form.Text className="text-muted">
                Minimum 1 adult required
              </Form.Text>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Number of Children</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={formData.children}
                onChange={(e) => {
                  const childrenCount = parseInt(e.target.value) || 0;
                  setFormData({...formData, children: childrenCount});
                }}
              />
            </Form.Group>
          </div>
        </div>

        {formData.children > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <Form.Label>Children's Ages</Form.Label>
            </div>
            {[...Array(formData.children)].map((_, index) => (
              <div className="col-md-3 col-6 mb-2" key={index}>
                <Form.Control
                  type="number"
                  placeholder={`Child ${index + 1} age`}
                  value={formData.childrenAges[index]}
                  onChange={(e) => handleChildAgeChange(index, e.target.value)}
                  min="0"
                  max="17"
                  required
                />
              </div>
            ))}
            <div className="col-12">
              <Form.Text className="text-muted">
                Please enter age for each child
              </Form.Text>
            </div>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Price Range (per night)</Form.Label>
          <Form.Select
            value={formData.priceRange}
            onChange={(e) => setFormData({...formData, priceRange: e.target.value})}
            required
          >
            <option value="">Select Price Range</option>
            {priceRanges.map((range, index) => (
              <option key={index} value={range}>{range}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Room Type</Form.Label>
          <Form.Select
            value={formData.roomType}
            onChange={(e) => setFormData({...formData, roomType: e.target.value})}
            required
          >
            <option value="">Select Room Type</option>
            {roomTypes.map((type, index) => (
              <option key={index} value={type}>{type}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Special Requests</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={formData.specialRequests}
            onChange={(e) => setFormData({...formData, specialRequests: e.target.value})}
          />
        </Form.Group>

        <Button 
          type="submit" 
          style={{
            backgroundColor: '#D4AF37',
            border: 'none',
            width: '100%',
            padding: '12px'
          }}
        >
          Submit Booking Request
        </Button>
      </Form>
    </div>
  );
};

export default HotelBookingForm;

